import { Card, CardContent, CardHeader, CardTitle } from '@ui/card.tsx';
import { Mail, MousePointer2, MousePointerClick } from 'lucide-react';
import { StatBrick } from '@admin/components/stat-brick.tsx';
import { BarChartBloc } from '@r/components/kpi/points/bar-chart-bloc.tsx';
import type { FormattedDataForRendering } from '@r/components/kpi/points/bar-chart-bloc.tsx';
import { UserLoginHistoryTable } from '@admin/pages/users/components/user-login-history-table.tsx';
import { Button } from '@r/components/ui/button.tsx';
import { useTranslation } from 'react-i18next';

type UserLoginStatsCardProps = {
  title: string;
  loginCountTitle: string;
  chartInfo: string;
  loginCount: number;
  formattedLoginCountPerMonth: FormattedDataForRendering[];
  userId: string;
  userUniqueCode: string;
  onSendMagicLink?: () => void;
  isSendingMagicLink: boolean;
};

export function UserLoginStatsCard({
  title,
  loginCountTitle,
  chartInfo,
  loginCount,
  formattedLoginCountPerMonth,
  userId,
  userUniqueCode,
  onSendMagicLink,
  isSendingMagicLink
}: UserLoginStatsCardProps) {
  const { t } = useTranslation();

  return (
    <Card className="flex flex-col">
      <CardHeader className="p-10">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center space-x-2 text-xl font-author-complete font-regular">
            <MousePointer2 />
            <p className="text-xl font-bold">{title}</p>
          </CardTitle>
          {onSendMagicLink && (
            <Button rounded="full" onClick={onSendMagicLink} disabled={isSendingMagicLink}>
              <Mail className="w-4 h-4" />
              {t('admin.user.profile.magic.link.button')}
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="flex flex-col p-10 pt-0 space-y-6">
        <div className="pl-6">
          <StatBrick
            title={loginCountTitle}
            icon={<MousePointerClick size={20} />}
            value={loginCount}
            valueClassName="text-4xl"
            cardMode={false}
            hasValidData={true}
          />
        </div>

        {formattedLoginCountPerMonth.length > 0 && (
          <div className="space-y-2">
            <BarChartBloc
              formattedDataPerMonth={formattedLoginCountPerMonth}
              dataType="quantity"
              classname="h-[200px]"
            />
            <p className="text-sm font-light text-center text-muted-foreground">{chartInfo}</p>
          </div>
        )}

        <UserLoginHistoryTable userId={userId} userUniqueCode={userUniqueCode} />
      </CardContent>
    </Card>
  );
}
